import type { KLinePoint, KLineConfig } from '../types'

/**
 * 生成合成 K 线数据
 * 用于教学场景，不需要依赖真实历史数据
 */

export interface KLineScenario {
  preset: string
  description: string
  generate: () => KLinePoint[]
}

/**
 * 场景 1: 完美 B1 建仓波
 * 走势：下跌后缩量盘整 → 放量突破 → 缩量回调 → 再次放量启动
 */
const perfectB1Scenario: KLineScenario = {
  preset: 'perfect-b1',
  description: '完美 B1 建仓波：底部放量 + 缩量回调 + 再次突破',
  generate: () => {
    const data: KLinePoint[] = []
    let price = 10.0

    // Day 1-3: 缩量下跌
    for (let i = 1; i <= 3; i++) {
      const change = -0.15 + (Math.random() - 0.5) * 0.1
      price += change
      data.push({
        day: i,
        open: price - change,
        close: price,
        high: price + 0.1,
        low: price - 0.15,
        volume: 800000 - i * 100000,
      })
    }

    // Day 4-6: 缩量盘整（建仓期）
    for (let i = 4; i <= 6; i++) {
      const change = (Math.random() - 0.5) * 0.05
      price += change
      data.push({
        day: i,
        open: price - change,
        close: price,
        high: price + 0.08,
        low: price - 0.08,
        volume: 500000,
      })
    }

    // Day 7: 放量突破
    price += 0.3
    data.push({
      day: 7,
      open: price - 0.3,
      close: price,
      high: price + 0.05,
      low: price - 0.25,
      volume: 1500000,
    })

    // Day 8-9: 缩量回调
    for (let i = 8; i <= 9; i++) {
      const change = (Math.random() - 0.3) * 0.08
      price += change
      data.push({
        day: i,
        open: price - change,
        close: price,
        high: price + 0.05,
        low: price - 0.05,
        volume: 600000,
      })
    }

    // Day 10: 再次放量启动
    price += 0.25
    data.push({
      day: 10,
      open: price - 0.25,
      close: price,
      high: price + 0.05,
      low: price - 0.2,
      volume: 1800000,
    })

    return data
  },
}

/**
 * 场景 2: 跌破白线（高风险信号）
 * 走势：缓涨 → 跌破白线 → 继续下跌
 */
const breakWhiteLineScenario: KLineScenario = {
  preset: 'break-white-line',
  description: '跌破白线：高风险区警示',
  generate: () => {
    const data: KLinePoint[] = []
    let price = 10.0

    // Day 1-4: 缓涨
    for (let i = 1; i <= 4; i++) {
      const change = 0.1 + (Math.random() - 0.5) * 0.05
      price += change
      data.push({
        day: i,
        open: price - change,
        close: price,
        high: price + 0.05,
        low: price - 0.05,
        volume: 700000 + i * 50000,
      })
    }

    // Day 5: 放量下跌，跌破白线
    price -= 0.35
    data.push({
      day: 5,
      open: price + 0.35,
      close: price,
      high: price + 0.4,
      low: price - 0.05,
      volume: 1400000,
    })

    // Day 6: 继续下跌
    price -= 0.2
    data.push({
      day: 6,
      open: price + 0.2,
      close: price,
      high: price + 0.05,
      low: price - 0.1,
      volume: 1100000,
    })

    // Day 7: 弱势整理
    const change = (Math.random() - 0.5) * 0.05
    price += change
    data.push({
      day: 7,
      open: price - change,
      close: price,
      high: price + 0.05,
      low: price - 0.05,
      volume: 800000,
    })

    return data
  },
}

/**
 * 场景 3: 假 B2 突破（追高陷阱）
 * 走势：缩量上涨 → 突然放量突破 → 第二天直接回落
 */
const fakeB2Scenario: KLineScenario = {
  preset: 'fake-b2',
  description: '假 B2 突破：诱多陷阱',
  generate: () => {
    const data: KLinePoint[] = []
    let price = 10.0

    // Day 1-3: 缩量上涨
    for (let i = 1; i <= 3; i++) {
      const change = 0.08 + (Math.random() - 0.5) * 0.03
      price += change
      data.push({
        day: i,
        open: price - change,
        close: price,
        high: price + 0.03,
        low: price - 0.03,
        volume: 600000,
      })
    }

    // Day 4: 突然放量突破（诱多）
    price += 0.4
    data.push({
      day: 4,
      open: price - 0.4,
      close: price,
      high: price + 0.05,
      low: price - 0.3,
      volume: 2000000,
    })

    // Day 5: 直接回落（陷阱揭晓）
    price -= 0.3
    data.push({
      day: 5,
      open: price + 0.3,
      close: price,
      high: price + 0.05,
      low: price - 0.1,
      volume: 1800000,
    })

    // Day 6: 继续下跌
    price -= 0.15
    data.push({
      day: 6,
      open: price + 0.15,
      close: price,
      high: price + 0.03,
      low: price - 0.08,
      volume: 900000,
    })

    return data
  },
}

/**
 * 场景 4: 震荡市（横盘）
 */
const consolidationScenario: KLineScenario = {
  preset: 'consolidation',
  description: '震荡市：横盘整理，方向不明',
  generate: () => {
    const data: KLinePoint[] = []
    let price = 10.0

    for (let i = 1; i <= 7; i++) {
      const change = (Math.random() - 0.5) * 0.15
      price += change
      data.push({
        day: i,
        open: price - change,
        close: price,
        high: price + 0.08,
        low: price - 0.08,
        volume: 500000 + Math.floor(Math.random() * 200000),
      })
    }

    return data
  },
}

/**
 * 场景 5: 完美五分制（健康持仓）
 * 走势：缓涨 → 站上 BBI → 持续上涨
 */
const healthyPositionScenario: KLineScenario = {
  preset: 'healthy-position',
  description: '健康持仓：N 型向上，BBI 上方',
  generate: () => {
    const data: KLinePoint[] = []
    let price = 10.0

    for (let i = 1; i <= 8; i++) {
      const change = 0.12 + (Math.random() - 0.5) * 0.05
      price += change
      data.push({
        day: i,
        open: price - change,
        close: price,
        high: price + 0.05,
        low: price - 0.03,
        volume: 800000 + i * 50000,
      })
    }

    return data
  },
}

const scenarios: Record<string, KLineScenario> = {
  'perfect-b1': perfectB1Scenario,
  'break-white-line': breakWhiteLineScenario,
  'fake-b2': fakeB2Scenario,
  'consolidation': consolidationScenario,
  'healthy-position': healthyPositionScenario,
}

/**
 * 根据 preset 获取 K 线场景
 */
export function getKLineScenario(preset: string): KLineScenario | null {
  return scenarios[preset] ?? null
}

/**
 * 生成 K 线配置（按 preset）
 */
export function generateKLineConfig(preset: string): KLineConfig | null {
  const scenario = getKLineScenario(preset)
  if (!scenario) return null

  return {
    preset,
    data: scenario.generate(),
    indicators: ['bbi', 'white_line', 'yellow_line'],
  }
}

/**
 * 计算 BBI（多空指数）
 * BBI = (MA3 + MA6 + MA12 + MA24) / 4
 */
export function calculateBBI(data: KLinePoint[]): number[] {
  const periods = [3, 6, 12, 24]
  const bbiValues: number[] = []

  for (let i = 0; i < data.length; i++) {
    const idx = i + 1
    if (idx < 3) {
      bbiValues.push(NaN)
      continue
    }

    const mas = periods.map(period => {
      if (idx < period) return NaN
      const slice = data.slice(i - period + 1, i + 1)
      const sum = slice.reduce((acc, p) => acc + p.close, 0)
      return sum / period
    })

    if (mas.every(m => !isNaN(m))) {
      bbiValues.push(mas.reduce((a, b) => a + b, 0) / 4)
    } else {
      bbiValues.push(NaN)
    }
  }

  return bbiValues
}